/**
 * V165 — Studio Inspiration Strip
 * Adds content density to the otherwise empty Studio Create hub:
 *  - "Pre teba dnes" — quick-start scenarios (Black Friday, Launch, etc.)
 *  - "Najpopulárnejšie šablóny" — top trending templates (live from API)
 *  - "Tvoje nedávne práce" — last few designs (only shown if user has any)
 *
 * Pure presentation — fetches its own data, gracefully degrades when API is
 * empty or user is not logged in.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Flame, ShoppingBag, Calendar, Sparkles, PartyPopper,
  Rocket, Tag, Gift, Snowflake,
} from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const SCENARIO_PALETTE = [
  { key: "blackfriday", icon: Tag, accent: "linear-gradient(135deg,#ef4444,#7c2d12)",
    tQ: "scen_bf_title", titleFallback: "Black Friday",
    tS: "scen_bf_sub", subFallback: "Konvertujte zľavy na predaj — 5 hotových sad",
    pack: "blackfriday" },
  { key: "launch", icon: Rocket, accent: "linear-gradient(135deg,#a855f7,#6366f1)",
    tQ: "scen_launch_title", titleFallback: "Launch dňa",
    tS: "scen_launch_sub", subFallback: "Predstavte nový produkt s wow efektom",
    pack: "launch" },
  { key: "holiday", icon: Snowflake, accent: "linear-gradient(135deg,#06b6d4,#1e40af)",
    tQ: "scen_holiday_title", titleFallback: "Sviatočná kampaň",
    tS: "scen_holiday_sub", subFallback: "Vianoce, Veľká Noc, Deň Matiek — pripravené",
    pack: "holiday" },
  { key: "sale", icon: ShoppingBag, accent: "linear-gradient(135deg,#f59e0b,#ea580c)",
    tQ: "scen_sale_title", titleFallback: "Výpredaj",
    tS: "scen_sale_sub", subFallback: "Sezónne -30%, -50%, -70% kampane",
    pack: "sale" },
  { key: "event", icon: PartyPopper, accent: "linear-gradient(135deg,#ec4899,#db2777)",
    tQ: "scen_event_title", titleFallback: "Event / Konferencia",
    tS: "scen_event_sub", subFallback: "Webinár, meetup, demo deň",
    pack: "event" },
  { key: "giveaway", icon: Gift, accent: "linear-gradient(135deg,#10b981,#047857)",
    tQ: "scen_giveaway_title", titleFallback: "Súťaž / Giveaway",
    tS: "scen_giveaway_sub", subFallback: "Engagement push s odmenou",
    pack: "giveaway" },
];

export default function StudioInspirationStrip() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [packCounts, setPackCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Trending: top usage_count, animated/light payload
        const tRes = await axios.get(`${API}/ads-creator/templates`, {
          params: { limit: 8, light: true },
        });
        if (alive) setTrending(tRes.data?.templates || []);
      } catch { /* ignore */ }

      try {
        // Per-scenario counts so we can show "239 šablón" badges
        const pRes = await axios.get(`${API}/ads-creator/template-packs/stats`);
        if (alive) setPackCounts(pRes.data?.counts || {});
      } catch { /* ignore */ }

      if (user?.email) {
        try {
          const myRes = await axios.get(`${API}/ads-creator/projects`, {
            params: { email: user.email, limit: 4 },
            withCredentials: true,
          });
          if (alive) setRecent(myRes.data?.projects || myRes.data?.items || []);
        } catch { /* ignore */ }
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.email]);

  // Resolve i18n labels with fallbacks
  const scenarios = useMemo(() => SCENARIO_PALETTE.map(s => ({
    ...s,
    title: t[s.tQ] || s.titleFallback,
    sub: t[s.tS] || s.subFallback,
  })), [t]);

  const handleTemplateClick = (tpl) => {
    navigate(`/ads-creator/editor?type=${tpl.format || "post"}&template=${tpl.template_id || tpl.id}`);
  };

  const handleScenarioClick = (scn) => {
    navigate(`/ads-creator/templates?scenario_pack=${encodeURIComponent(scn.pack)}`);
  };

  return (
    <div data-testid="studio-inspiration" style={{ marginTop: 48 }}>
      {/* ─── Quick-start scenarios ──────────────────────────────────── */}
      <SectionHeader
        icon={<Flame size={14} />}
        eyebrow={t.studio_strip_scenarios_eyebrow || "Pre teba dnes"}
        title={t.studio_strip_scenarios_title || "Začni z scenára"}
        sub={t.studio_strip_scenarios_sub || "Vyber typ kampane → otvoríme ti pripravené šablóny"}
      />
      <div
        data-testid="studio-strip-scenarios"
        style={{
          display: "grid", gap: 12, marginTop: 16,
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
        }}
      >
        {scenarios.map((scn) => {
          const count = packCounts[scn.pack] || 0;
          return (
          <button
            type="button"
            key={scn.key}
            data-testid={`studio-scenario-${scn.key}`}
            onClick={() => handleScenarioClick(scn)}
            disabled={count === 0}
            style={{
              padding: 16, textAlign: "left",
              cursor: count > 0 ? "pointer" : "not-allowed",
              opacity: count > 0 ? 1 : 0.45,
              background: "rgba(20,20,35,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 14, color: "#fff",
              transition: "all 180ms ease",
              display: "flex", flexDirection: "column", gap: 10,
              position: "relative",
            }}
            onMouseEnter={(e) => {
              if (count > 0) {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.borderColor = "rgba(168,85,247,0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.borderColor = "";
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: scn.accent, display: "inline-flex",
                alignItems: "center", justifyContent: "center", color: "#fff",
              }}>
                <scn.icon size={18} />
              </div>
              {count > 0 ? (
                <span style={{
                  fontSize: 10, fontWeight: 800, color: "#c084fc",
                  background: "rgba(168,85,247,0.12)",
                  padding: "3px 7px", borderRadius: 4, letterSpacing: "0.05em",
                }}>
                  {count} {t.studio_strip_templates_short || "šablón"}
                </span>
              ) : (
                <span style={{
                  fontSize: 9, fontWeight: 800, color: "#888",
                  background: "rgba(255,255,255,0.06)",
                  padding: "3px 7px", borderRadius: 4, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}>
                  {t.studio_strip_coming_soon || "Soon"}
                </span>
              )}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>
              {scn.title}
            </div>
            <div style={{ fontSize: 12, color: "#a5a5c0", lineHeight: 1.45 }}>
              {scn.sub}
            </div>
            <div style={{
              marginTop: "auto", display: "inline-flex", alignItems: "center",
              gap: 4, color: count > 0 ? "#c084fc" : "#666", fontSize: 11, fontWeight: 800,
            }}>
              {count > 0 ? (t.studio_strip_open || "Otvoriť") : (t.studio_strip_notify || "Pripravujeme")} <ArrowRight size={12} />
            </div>
          </button>
          );
        })}
      </div>

      {/* ─── Recently used (only if user has projects) ──────────────── */}
      {recent.length > 0 && (
        <>
          <div style={{ marginTop: 40 }}>
            <SectionHeader
              icon={<Calendar size={14} />}
              eyebrow={t.studio_strip_recent_eyebrow || "Pokračuj"}
              title={t.studio_strip_recent_title || "Tvoje nedávne práce"}
              sub={t.studio_strip_recent_sub || "Pokračuj tam kde si skončil"}
              cta={{
                label: t.studio_strip_view_all || "Všetky →",
                onClick: () => navigate("/ads-creator/my-ads"),
                testid: "studio-strip-recent-all",
              }}
            />
          </div>
          <div
            data-testid="studio-strip-recent"
            style={{
              display: "grid", gap: 12, marginTop: 16,
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            }}
          >
            {recent.slice(0, 4).map((proj) => (
              <button
                type="button"
                key={proj.project_id || proj.id}
                data-testid={`studio-recent-${proj.project_id || proj.id}`}
                onClick={() => navigate(`/ads-creator/editor?project=${proj.project_id || proj.id}`)}
                style={{
                  padding: 0, textAlign: "left", cursor: "pointer",
                  background: "rgba(20,20,35,0.6)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 12, color: "#fff", overflow: "hidden",
                  transition: "transform 180ms ease, border-color 180ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = "rgba(168,85,247,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.borderColor = "";
                }}
              >
                <div style={{
                  aspectRatio: "4/5", background: proj.thumbnail_url
                    ? `center/cover no-repeat url(${proj.thumbnail_url})`
                    : "linear-gradient(135deg,#222,#111)",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                }} />
                <div style={{ padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "-0.01em",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {proj.title || "Untitled"}
                  </div>
                  <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                    {proj.format || "post"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ─── Trending templates ─────────────────────────────────────── */}
      {!loading && trending.length > 0 && (
        <>
          <div style={{ marginTop: 40 }}>
            <SectionHeader
              icon={<Sparkles size={14} />}
              eyebrow={t.studio_strip_trending_eyebrow || "Najpopulárnejšie"}
              title={t.studio_strip_trending_title || "Trending šablóny"}
              sub={t.studio_strip_trending_sub || "Šablóny ktoré tento týždeň fungujú"}
              cta={{
                label: t.studio_strip_view_all || "Všetky →",
                onClick: () => navigate("/ads-creator/templates"),
                testid: "studio-strip-trending-all",
              }}
            />
          </div>
          <div
            data-testid="studio-strip-trending"
            style={{
              display: "grid", gap: 12, marginTop: 16,
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            }}
          >
            {trending.slice(0, 8).map((tpl) => (
              <button
                type="button"
                key={tpl.template_id || tpl.id || tpl.title}
                data-testid={`studio-trending-${tpl.template_id || tpl.id || ""}`}
                onClick={() => handleTemplateClick(tpl)}
                style={{
                  padding: 0, textAlign: "left", cursor: "pointer",
                  background: "rgba(20,20,35,0.6)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 12, color: "#fff", overflow: "hidden",
                  transition: "transform 180ms ease, border-color 180ms ease",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = "rgba(168,85,247,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.borderColor = "";
                }}
              >
                <div style={{
                  aspectRatio: tpl.format === "facebook_story" ? "9/16" : "1/1",
                  background: tpl.preview_url
                    ? `center/cover no-repeat url(${tpl.preview_url})`
                    : "linear-gradient(135deg,#1f1f3a,#0e0e22)",
                }} />
                {tpl.premium && (
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    background: "linear-gradient(135deg,#fbbf24,#d97706)",
                    color: "#000", fontSize: 9, fontWeight: 900,
                    padding: "2px 6px", borderRadius: 4, letterSpacing: "0.05em",
                  }}>PRO</div>
                )}
                {tpl.animated && (
                  <div style={{
                    position: "absolute", top: 8, left: 8,
                    background: "rgba(0,0,0,0.7)",
                    color: "#fff", fontSize: 9, fontWeight: 800,
                    padding: "2px 6px", borderRadius: 4, letterSpacing: "0.05em",
                  }}>▶ ANIM</div>
                )}
                <div style={{ padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "-0.01em",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {tpl.title || "Template"}
                  </div>
                  <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                    {tpl.format || ""}{tpl.usage_count ? ` · ${tpl.usage_count} použití` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ icon, eyebrow, title, sub, cta }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
      <div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          color: "#c084fc", fontSize: 10, fontWeight: 800,
          letterSpacing: "0.18em", textTransform: "uppercase",
          marginBottom: 6,
        }}>
          {icon}
          {eyebrow}
        </div>
        <h2 style={{
          color: "#fff", fontSize: 22, fontWeight: 900,
          letterSpacing: "-0.02em", margin: "0 0 4px",
        }}>{title}</h2>
        <p style={{ color: "#a5a5c0", fontSize: 13, margin: 0 }}>{sub}</p>
      </div>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          data-testid={cta.testid}
          style={{
            background: "transparent", border: 0, color: "#c084fc",
            fontSize: 12, fontWeight: 800, cursor: "pointer", padding: "8px 12px",
          }}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
